import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Breadcrumb } from "@/components/Common/Breadcrumb";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import {
  useGetMyOrganization,
  useGetOrganizationMembers,
} from "@/hooks/useOrganizations";
import { FREE_PLAN_NAME } from "@/lib/constants";
import { humanizeString } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Crown,
  Mail,
  MoreHorizontal,
  Settings as SettingsIcon,
  Shield,
  UserPlus,
  Users,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const inviteSchema = z.object({
  email: z.string().email("Invalid email address"),
  role: z.enum(["admin", "member"]),
});

type InviteFormData = z.infer<typeof inviteSchema>;

export function TeamManagementPage() {
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const { toast } = useToast();
  const orgMembersQuery = useGetOrganizationMembers();
  const orgQuery = useGetMyOrganization();
  const members = orgMembersQuery.data.filter((m) => m.inviteAccepted);
  const pendingMembers = orgMembersQuery.data.filter((m) => !m.inviteAccepted);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
  });

  const onInvite = async (data: InviteFormData) => {
    // Mock API call
    await new Promise((resolve) => setTimeout(resolve, 1000));

    toast({
      title: "Invitation sent",
      description: `Invitation sent to ${data.email} as ${data.role}`,
    });

    setShowInviteDialog(false);
    reset();
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case "owner":
        return <Crown className="h-3 w-3 lg:h-4 lg:w-4 text-yellow-400" />;
      case "admin":
        return <Shield className="h-3 w-3 lg:h-4 lg:w-4 text-blue-400" />;
      case "developer":
        return <Users className="h-3 w-3 lg:h-4 lg:w-4 text-slate-400" />;
    }
  };

  const getRoleBadge = (role: string) => {
    const colors = {
      owner: "text-yellow-400 bg-yellow-400/10 border-yellow-400/30",
      admin: "text-blue-400 bg-blue-400/10 border-blue-400/30",
      developer: "text-slate-400 bg-slate-400/10 border-slate-400/30",
    };

    return (
      <Badge
        className={`${
          colors[role as keyof typeof colors]
        }  flex items-center gap-1 text-xs`}
      >
        {getRoleIcon(role)}
        {humanizeString(role)}
      </Badge>
    );
  };

  const breadcrumbs = useMemo(() => {
    return [{ label: "teams" }];
  }, []);

  return (
    <div className="min-h-full">
      <div className="hidden lg:block border-b border-slate-800 bg-slate-900/90">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-5">
          <Breadcrumb items={breadcrumbs} />
        </div>
      </div>
      <div className="p-4 lg:p-8 space-y-6 lg:space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 pt-12 lg:pt-0">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-cyan-400  mb-2">
              Team Management
            </h1>
            <p className="text-slate-300  text-sm lg:text-base">
              Manage team members and their permissions
            </p>
          </div>

          <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
            <DialogTrigger asChild>
              <Button
                disabled={orgQuery.data.plan.name === FREE_PLAN_NAME}
                className="cyber-button  text-sm w-full sm:w-auto"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Invite Member
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-cyan-500/30 w-[95vw] max-w-md">
              <DialogHeader>
                <DialogTitle className="text-cyan-400 ">
                  Invite Team Member
                </DialogTitle>
                <DialogDescription className="text-slate-400 ">
                  Send an invitation to join your organization
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit(onInvite)} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-cyan-300 ">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    {...register("email")}
                    placeholder="colleague@example.com"
                    className="bg-slate-800/50 border-cyan-500/30 text-cyan-100  focus:border-cyan-400"
                  />
                  {errors.email && (
                    <p className="text-red-400 text-xs ">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role" className="text-cyan-300 ">
                    Role
                  </Label>
                  <Select
                    onValueChange={(value) =>
                      register("role").onChange({ target: { value } })
                    }
                  >
                    <SelectTrigger className="bg-slate-800/50 border-cyan-500/30 text-cyan-100 ">
                      <SelectValue placeholder="Select a role" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="member" className="text-slate-300 ">
                        Member
                      </SelectItem>
                      <SelectItem value="admin" className="text-slate-300 ">
                        Admin
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowInviteDialog(false)}
                    className=""
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="cyber-button ">
                    <Mail className="h-4 w-4 mr-2" />
                    Send Invitation
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Team Members */}
        <Card className="holo-card">
          <CardHeader>
            <CardTitle className="text-cyan-400  text-lg lg:text-xl">
              Team Members
            </CardTitle>
            <CardDescription className="text-slate-400  text-sm">
              Current members of your organization
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700/50">
                    <TableHead className="text-slate-400  text-xs lg:text-sm">
                      Member
                    </TableHead>
                    <TableHead className="text-slate-400  text-xs lg:text-sm">
                      Role
                    </TableHead>
                    <TableHead className="text-slate-400  text-xs lg:text-sm hidden sm:table-cell">
                      Joined
                    </TableHead>
                    <TableHead className="text-slate-400  text-xs lg:text-sm">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((member) => (
                    <TableRow key={member.id} className="border-slate-700/50">
                      <TableCell>
                        <div className="flex items-center gap-2 lg:gap-3">
                          <div className="h-6 w-6 lg:h-8 lg:w-8 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center flex-shrink-0">
                            <span className="text-slate-900  text-xs lg:text-sm font-bold">
                              {member.name.charAt(0)}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-cyan-100  text-xs lg:text-sm truncate">
                              {member.name}
                            </p>
                            <p className="text-xs text-slate-400  truncate">
                              {member.email}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {member.roles.map((r) => getRoleBadge(r))}
                      </TableCell>
                      <TableCell className="text-slate-300  text-xs lg:text-sm hidden sm:table-cell">
                        {new Date(member.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {!member.roles.includes("owner") && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-slate-400 hover:text-cyan-400 h-8 w-8 p-0"
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="bg-slate-800 border-slate-700"
                            >
                              <DropdownMenuItem className="text-slate-300 hover:text-cyan-400  text-xs">
                                <SettingsIcon className="h-4 w-4 mr-2" />
                                Change Role
                              </DropdownMenuItem>
                              <DropdownMenuItem className="text-red-400 hover:text-red-300  text-xs">
                                Remove Member
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Pending Invitations */}
        <Card className="holo-card">
          <CardHeader>
            <CardTitle className="text-cyan-400  text-lg lg:text-xl">
              Pending Invitations
            </CardTitle>
            <CardDescription className="text-slate-400  text-sm">
              Invitations that haven't been accepted yet
            </CardDescription>
          </CardHeader>
          <CardContent>
            {pendingMembers.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700/50">
                      <TableHead className="text-slate-400  text-xs lg:text-sm">
                        Email
                      </TableHead>
                      <TableHead className="text-slate-400  text-xs lg:text-sm">
                        Role
                      </TableHead>

                      <TableHead className="text-slate-400  text-xs lg:text-sm hidden sm:table-cell">
                        Expires
                      </TableHead>
                      <TableHead className="text-slate-400  text-xs lg:text-sm">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingMembers.map((invitation) => (
                      <TableRow
                        key={invitation.id}
                        className="border-slate-700/50"
                      >
                        <TableCell className="text-cyan-100  text-xs lg:text-sm">
                          {invitation.email}
                        </TableCell>
                        <TableCell>
                          {invitation.roles.map((r) => getRoleBadge(r))}
                        </TableCell>
                        <TableCell className="text-slate-300  text-xs lg:text-sm hidden sm:table-cell">
                          {
                            new Date(invitation.createdAt).toLocaleDateString()
                            // TODO add invite logic
                          }
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300  text-xs"
                          >
                            Revoke
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400 ">
                No pending invitations
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
